"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { X, Loader2, Sparkles, Trash2, Upload, GripVertical, Plus, ChevronDown, ChevronRight, FileText, Square } from "lucide-react";
import { ecomImageApi, InspireTaskResult, InspireTemplate } from "../../lib/ecom-image-api";
import { isImageFile, IMAGE_ACCEPT } from "../../lib/file-utils";
import { getSafeErrorMessage, getErrorMessage } from "../../lib/error-codes";
import { EcomShimmerText } from "../../ui/shimmer-text";
import { AutoResizeTextarea } from "./AutoResizeTextarea";
import { createClientUUID } from "../../lib/client-uuid";
import {
  claimReversePromptOperation,
  clearReversePromptOperation,
  clearReversePromptOperations,
  getLatestReversePromptOperation,
  getReversePromptOperations,
  isReversePromptOperationActive,
  isReversePromptOperationCurrent,
  subscribeReversePromptOperations,
  updateReversePromptOperation,
  type ReversePromptOperationClaim,
  type ReversePromptOperationSnapshot,
} from "../../lib/ecom-reverse-prompt-lifecycle";

function sanitizeTaskError(raw?: string): string {
  if (!raw) return "反推处理失败，请稍后重试";
  const match = raw.match(/E(\d{4})/);
  if (match) {
    return getErrorMessage(parseInt(match[1], 10));
  }
  if (/timeout|超时/i.test(raw)) return "反推超时，请减少图片数量或稍后重试";
  if (/connection|connect|ECONNREFUSED|read.*reset/i.test(raw)) return "服务连接异常，请稍后重试";
  if (/download|failed to download/i.test(raw)) return "图片下载失败，请检查图片链接或稍后重试";
  if (/审核|review|content.*policy/i.test(raw)) return "内容审核未通过，请更换图片后重试";
  if (/404|not found/i.test(raw)) return "资源不存在，请检查图片链接";
  if (/500|internal.*error/i.test(raw)) return "服务器内部错误，请稍后重试";
  return "反推处理失败，请稍后重试";
}

interface InspirationPromptModalProps {
  open: boolean;
  onClose: () => void;
  acceptFrom: string[];
  batchCount: number;
  params: Record<string, unknown>;
  workflowKey: string;
  defaultTemplateId?: number;
  componentLabels?: Record<string, string>;
  reversePromptOwnerScope?: string;
  onApplyPrompts: (prompts: string[]) => void;
}

type BatchStatus = "pending" | "loading" | "done" | "error";

interface BatchImage {
  componentKey: string;
  url: string;
}

interface BatchItem {
  index: number;
  images: BatchImage[];
  prompt: string;
  status: BatchStatus;
  error?: string;
}

function getImageForBatch(images: string[], batchIndex: number): string | null {
  if (!images || images.length === 0) return null;
  if (batchIndex < images.length && images[batchIndex]) {
    return images[batchIndex];
  }
  return null;
}

export default function InspirationPromptModal({
  open,
  onClose,
  acceptFrom,
  batchCount,
  params,
  workflowKey,
  defaultTemplateId,
  componentLabels,
  reversePromptOwnerScope,
  onApplyPrompts,
}: InspirationPromptModalProps) {
  const [batches, setBatches] = useState<BatchItem[]>([]);
  const batchesRef = useRef<BatchItem[]>([]);
  const [allLoading, setAllLoading] = useState(false);
  const activeOperationRef = useRef<ReversePromptOperationClaim | null>(null);
  const batchOperationsRef = useRef<Map<number, ReversePromptOperationClaim>>(new Map());
  const pollAbortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const reversePromptChannelPrefix = `inspire:${workflowKey}:`;

  const commitBatches = useCallback((
    updater: BatchItem[] | ((current: BatchItem[]) => BatchItem[]),
    operation?: ReversePromptOperationClaim | null,
  ) => {
    const next = typeof updater === "function" ? updater(batchesRef.current) : updater;
    batchesRef.current = next;
    setBatches(next);
    const activeOperations = new Map<string, ReversePromptOperationClaim>();
    if (isReversePromptOperationActive(operation)) {
      activeOperations.set(operation.operationId, operation);
    }
    if (isReversePromptOperationActive(activeOperationRef.current)) {
      activeOperations.set(activeOperationRef.current.operationId, activeOperationRef.current);
    }
    for (const batchOperation of batchOperationsRef.current.values()) {
      if (isReversePromptOperationActive(batchOperation)) {
        activeOperations.set(batchOperation.operationId, batchOperation);
      }
    }
    activeOperations.forEach((activeOperation) => {
      updateReversePromptOperation(activeOperation, "polling", { batches: next });
    });
    return next;
  }, []);

  const releaseOperation = useCallback((operation: ReversePromptOperationClaim) => {
    pollAbortControllersRef.current.delete(operation.operationId);
    for (const [batchIndex, batchOperation] of batchOperationsRef.current) {
      if (batchOperation.operationId === operation.operationId) {
        batchOperationsRef.current.delete(batchIndex);
      }
    }
    if (activeOperationRef.current?.operationId === operation.operationId) {
      activeOperationRef.current = null;
    }
  }, []);

  // Template state
  const [templateExpanded, setTemplateExpanded] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [allTemplates, setAllTemplates] = useState<InspireTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [originalSystemPrompt, setOriginalSystemPrompt] = useState("");
  const [customSystemPrompt, setCustomSystemPrompt] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const templateSelectorRef = useRef<HTMLDivElement>(null);


  // Fetch templates on open
  useEffect(() => {
    if (!open) return;
    setTemplateLoading(true);

    Promise.all([
      ecomImageApi.getWorkflowInspireTemplate(workflowKey).catch(() => null),
      ecomImageApi.getInspireTemplates().catch(() => null),
    ]).then(([wfRes, allRes]) => {
      const templates = allRes?.data ?? [];
      setAllTemplates(templates);

      const boundTemplate = defaultTemplateId
        ? templates.find(t => t.id === defaultTemplateId)
        : undefined;
      const wfTemplate = boundTemplate || wfRes?.data;
      if (wfTemplate?.system_prompt) {
        setOriginalSystemPrompt(wfTemplate.system_prompt);
        setCustomSystemPrompt(wfTemplate.system_prompt);
        setTemplateName(wfTemplate.template_name || "默认模板");
        setSelectedTemplateId(wfTemplate.id);
      } else if (templates.length > 0) {
        const first = templates.find(t => t.is_active) || templates[0];
        setOriginalSystemPrompt(first.system_prompt);
        setCustomSystemPrompt(first.system_prompt);
        setTemplateName(first.template_name);
        setSelectedTemplateId(first.id);
      }
    }).finally(() => {
      setTemplateLoading(false);
    });
  }, [open, workflowKey, defaultTemplateId]);

  // Close template selector on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (templateSelectorRef.current && !templateSelectorRef.current.contains(e.target as Node)) {
        setShowTemplateSelector(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelectTemplate = useCallback((tmpl: InspireTemplate) => {
    setSelectedTemplateId(tmpl.id);
    setOriginalSystemPrompt(tmpl.system_prompt);
    setCustomSystemPrompt(tmpl.system_prompt);
    setTemplateName(tmpl.template_name);
    setShowTemplateSelector(false);
  }, []);

  useEffect(() => {
    if (!open) {
      sessionActiveRef.current = false;
      return;
    }
    if (sessionActiveRef.current) return;
    sessionActiveRef.current = true;

    const items: BatchItem[] = [];
    for (let i = 0; i < batchCount; i++) {
      const images: BatchImage[] = [];
      for (const key of acceptFrom) {
        const arr = params[key];
        if (Array.isArray(arr)) {
          if (arr.length > 0 && Array.isArray(arr[0])) {
            const batchImages = (arr as string[][])[i] || [];
            for (const url of batchImages) {
              if (url) images.push({ componentKey: key, url });
            }
          } else {
            const url = getImageForBatch(arr as string[], i);
            if (url) images.push({ componentKey: key, url });
          }
        } else if (typeof arr === "string" && arr) {
          images.push({ componentKey: key, url: arr });
        }
      }
      items.push({ index: i, images, prompt: "", status: "pending" });
    }
    const restored = getLatestReversePromptOperation<{ batches?: BatchItem[] }>(
      reversePromptOwnerScope,
      reversePromptChannelPrefix,
    );
    if (restored?.data?.batches?.length) {
      batchesRef.current = restored.data.batches;
      setBatches(restored.data.batches);
      const isRestoredActive = restored.phase === "submitting" || restored.phase === "polling";
        activeOperationRef.current = isRestoredActive && restored.channel === `${reversePromptChannelPrefix}all`
        ? restored
        : null;
      setAllLoading(isRestoredActive && restored.channel === `${reversePromptChannelPrefix}all`);
      return;
    }
    activeOperationRef.current = null;
    batchesRef.current = items;
    setBatches(items);
  }, [open, batchCount, acceptFrom, params, reversePromptChannelPrefix, reversePromptOwnerScope]);

  useEffect(() => subscribeReversePromptOperations(reversePromptOwnerScope, (incoming) => {
    const snapshot = incoming?.channel.startsWith(reversePromptChannelPrefix)
      ? incoming as ReversePromptOperationSnapshot<{ batches?: BatchItem[] }>
      : getLatestReversePromptOperation<{ batches?: BatchItem[] }>(
          reversePromptOwnerScope,
          reversePromptChannelPrefix,
        );
    const activeSnapshots = getReversePromptOperations<{ batches?: BatchItem[] }>(
      reversePromptOwnerScope,
      reversePromptChannelPrefix,
    ).filter((operation) => operation.phase === "submitting" || operation.phase === "polling");
    const activeAllOperation = activeSnapshots.find(
      (operation) => operation.channel === `${reversePromptChannelPrefix}all`,
    );
    activeOperationRef.current = activeAllOperation || activeSnapshots.at(-1) || null;
    if (snapshot?.data?.batches) {
      batchesRef.current = snapshot.data.batches;
      setBatches(snapshot.data.batches);
    }
    setAllLoading(Boolean(activeAllOperation));
  }), [reversePromptChannelPrefix, reversePromptOwnerScope]);

  const updateBatch = useCallback((
    index: number,
    patch: Partial<BatchItem>,
    operation?: ReversePromptOperationClaim | null,
  ) => commitBatches(
    (prev) => prev.map((b) => (b.index === index ? { ...b, ...patch } : b)),
    operation,
  ), [commitBatches]);

  const abortOperation = useCallback((operation: ReversePromptOperationClaim | null | undefined) => {
    if (!operation) return;
    pollAbortControllersRef.current.get(operation.operationId)?.abort();
    pollAbortControllersRef.current.delete(operation.operationId);
  }, []);

  const POLL_INTERVAL = 1500;
  const POLL_TIMEOUT = 240_000;
  const SINGLE_TASK_TIMEOUT = 50_000;
  const sessionActiveRef = useRef(false);

  const pollForResult = useCallback(async (
    taskId: string,
    _batchIndices: number[],
    onDone: (results: InspireTaskResult["results"]) => void,
    onError: (msg: string) => void,
    onPartial?: (results: InspireTaskResult["results"]) => void,
    operation?: ReversePromptOperationClaim | null,
  ) => {
    const startTime = Date.now();
    const controller = new AbortController();
    if (operation) pollAbortControllersRef.current.set(operation.operationId, controller);
    let lastSeenCount = 0;
    let consecutiveErrors = 0;

    const poll = async () => {
      while (!controller.signal.aborted && (!operation || isReversePromptOperationCurrent(operation))) {
        if (Date.now() - startTime > POLL_TIMEOUT) {
          onError("反推超时（4分钟），请减少图片数量或稍后重试");
          return;
        }
        try {
          const res = await ecomImageApi.getInspireBatchResult(taskId);
          if (controller.signal.aborted || (operation && !isReversePromptOperationCurrent(operation))) return;
          const task = res.data;
          if (!task) { onError("查询结果失败"); return; }
          consecutiveErrors = 0;

          const partialResults = task.results ?? [];
          if (onPartial && partialResults.length > lastSeenCount) {
            lastSeenCount = partialResults.length;
            onPartial(partialResults);
          }

          if (task.status === "completed") { onDone(partialResults); return; }
          if (task.status === "failed") { onError(sanitizeTaskError(task.error)); return; }
        } catch {
          if (controller.signal.aborted) return;
          consecutiveErrors++;
          if (consecutiveErrors > 10) {
            onError("查询结果连续失败，请重试");
            return;
          }
        }
        await new Promise((r) => setTimeout(r, POLL_INTERVAL));
      }
    };
    try {
      await poll();
    } finally {
      if (operation && pollAbortControllersRef.current.get(operation.operationId) === controller) {
        pollAbortControllersRef.current.delete(operation.operationId);
      }
    }
  }, []);

  const runSingleInspire = useCallback(async (batch: BatchItem) => {
    const operation = claimReversePromptOperation(
      reversePromptOwnerScope,
      `${reversePromptChannelPrefix}batch:${batch.index}`,
      createClientUUID(),
      { batches: batchesRef.current },
    );
    if (!operation) return;
    batchOperationsRef.current.set(batch.index, operation);
    const controller = new AbortController();
    pollAbortControllersRef.current.set(operation.operationId, controller);
    updateBatch(batch.index, { status: "loading", error: undefined }, operation);

    const attemptOnce = async (): Promise<{ ok: boolean; prompt?: string; error?: string }> => {
      try {
        const res = await ecomImageApi.submitInspireBatchPrompt({
          batches: [{
            batch_index: batch.index,
            images: batch.images.map((img) => ({
              component_key: img.componentKey,
              url: img.url,
              label: componentLabels?.[img.componentKey],
            })),
          }],
          workflow_key: workflowKey,
          ...(selectedTemplateId ? { default_template_id: selectedTemplateId } : {}),
          ...(customSystemPrompt && customSystemPrompt !== originalSystemPrompt
            ? { custom_system_prompt: customSystemPrompt }
            : {}),
        });
        const taskId = res.data?.task_id;
        if (!taskId) return { ok: false, error: "提交任务失败" };
        if (!updateReversePromptOperation(operation, "polling", { batches: batchesRef.current, taskId })) {
          return { ok: false, error: "已取消" };
        }

        const startTime = Date.now();
        let consecutiveErrors = 0;
        while (true) {
          if (controller.signal.aborted || !isReversePromptOperationActive(operation)) {
            return { ok: false, error: "已取消" };
          }
          if (Date.now() - startTime > SINGLE_TASK_TIMEOUT) return { ok: false, error: "反推超时" };
          try {
            const r = await ecomImageApi.getInspireBatchResult(taskId);
            consecutiveErrors = 0;
            const task = r.data;
            if (!task) return { ok: false, error: "查询结果失败" };
            if (task.status === "completed") {
              const result = task.results?.find((x) => x.batch_index === batch.index);
              if (result?.error) return { ok: false, error: sanitizeTaskError(result.error) };
              return { ok: true, prompt: result?.prompt ?? "" };
            }
            if (task.status === "failed") return { ok: false, error: sanitizeTaskError(task.error) };
          } catch {
            consecutiveErrors++;
            if (consecutiveErrors > 5) return { ok: false, error: "查询结果连续失败" };
          }
          await new Promise((r) => setTimeout(r, POLL_INTERVAL));
        }
      } catch (err: unknown) {
        return { ok: false, error: getSafeErrorMessage(err, "反推失败") };
      }
    };

    try {
      let result = await attemptOnce();
      if (!result.ok && result.error !== "已取消") {
        updateBatch(batch.index, { status: "loading", error: undefined }, operation);
        result = await attemptOnce();
      }
      if (result.ok && isReversePromptOperationActive(operation)) {
        const next = updateBatch(
          batch.index,
          { status: "done", prompt: result.prompt ?? "" },
          operation,
        );
        updateReversePromptOperation(operation, "completed", { batches: next });
      } else if (result.error !== "已取消" && isReversePromptOperationActive(operation)) {
        const next = updateBatch(
          batch.index,
          { status: "error", error: result.error || "反推失败，请重试" },
          operation,
        );
        updateReversePromptOperation(operation, "failed", { batches: next, errorCode: "E5302" });
      }
    } finally {
      releaseOperation(operation);
    }
  }, [workflowKey, updateBatch, customSystemPrompt, originalSystemPrompt, componentLabels, releaseOperation, reversePromptChannelPrefix, reversePromptOwnerScope, selectedTemplateId]);

  const handleBatchImagesChange = useCallback((batchIndex: number, newImages: BatchImage[]) => {
    commitBatches((prev) =>
      prev.map((b) =>
        b.index === batchIndex
          ? { ...b, images: newImages, status: "pending" as BatchStatus, prompt: "", error: undefined }
          : b
      )
    );
  }, [commitBatches]);

  // Helper: submit a batch request and poll, returning final batch states
  const submitAndPoll = useCallback(async (
    targetBatches: BatchItem[],
    operation: ReversePromptOperationClaim,
  ): Promise<Map<number, { status: BatchStatus; prompt?: string; error?: string }>> => {
    const resultMap = new Map<number, { status: BatchStatus; prompt?: string; error?: string }>();
    if (!isReversePromptOperationCurrent(operation)) return resultMap;

    const batchPayload = targetBatches.map((b) => ({
      batch_index: b.index,
      images: b.images.map((img) => ({
        component_key: img.componentKey,
        url: img.url,
        label: componentLabels?.[img.componentKey],
      })),
    }));

    try {
      const res = await ecomImageApi.submitInspireBatchPrompt({
        batches: batchPayload,
        workflow_key: workflowKey,
        ...(selectedTemplateId ? { default_template_id: selectedTemplateId } : {}),
        ...(customSystemPrompt && customSystemPrompt !== originalSystemPrompt
          ? { custom_system_prompt: customSystemPrompt }
          : {}),
      });
      if (!isReversePromptOperationCurrent(operation)) return resultMap;
      const taskId = res.data?.task_id;
      if (!taskId) {
        for (const b of targetBatches) {
          resultMap.set(b.index, { status: "error", error: "提交任务失败" });
        }
        return resultMap;
      }
      if (!updateReversePromptOperation(operation, "polling", { batches: batchesRef.current, taskId })) {
        return resultMap;
      }

      await pollForResult(
        taskId,
        targetBatches.map((b) => b.index),
        (results) => {
          const arr = results ?? [];
          for (const b of targetBatches) {
            const r = arr.find((x) => x.batch_index === b.index);
            if (r?.error) {
              resultMap.set(b.index, { status: "error", error: sanitizeTaskError(r.error) });
            } else if (r) {
              resultMap.set(b.index, { status: "done", prompt: r.prompt || "" });
            } else if (!resultMap.has(b.index)) {
              resultMap.set(b.index, { status: "error", error: "未返回结果" });
            }
          }
        },
        (msg) => {
          for (const b of targetBatches) {
            if (!resultMap.has(b.index)) {
              resultMap.set(b.index, { status: "error", error: msg });
            }
          }
        },
        (partialResults) => {
          commitBatches((prev) =>
            prev.map((b) => {
              if (b.status !== "loading") return b;
              const r = (partialResults ?? []).find((x) => x.batch_index === b.index);
              if (!r) return b;
              if (r.error) {
                const safeMsg = sanitizeTaskError(r.error);
                resultMap.set(b.index, { status: "error", error: safeMsg });
                return { ...b, status: "error" as BatchStatus, error: safeMsg };
              }
              resultMap.set(b.index, { status: "done", prompt: r.prompt ?? "" });
              return { ...b, status: "done" as BatchStatus, prompt: r.prompt ?? "" };
            })
          );
        },
        operation,
      );
    } catch (err: unknown) {
      if (!isReversePromptOperationCurrent(operation)) return resultMap;
      const msg = getSafeErrorMessage(err, "批量反推失败，请重试");
      for (const b of targetBatches) {
        if (!resultMap.has(b.index)) {
          resultMap.set(b.index, { status: "error", error: msg });
        }
      }
    }

    return resultMap;
  }, [workflowKey, pollForResult, componentLabels, commitBatches, customSystemPrompt, originalSystemPrompt, selectedTemplateId]);

  const runAllInspire = useCallback(async () => {
    const operation = claimReversePromptOperation(
      reversePromptOwnerScope,
      `${reversePromptChannelPrefix}all`,
      createClientUUID(),
      { batches: batchesRef.current },
    );
    if (!operation) return;
    activeOperationRef.current = operation;
    setAllLoading(true);
    for (const batchOperation of batchOperationsRef.current.values()) {
      abortOperation(batchOperation);
      updateReversePromptOperation(batchOperation, "cancelled", { batches: batchesRef.current });
    }
    batchOperationsRef.current.clear();

    // Mark all as loading
    commitBatches((prev) => prev.map((b) => ({ ...b, status: "loading" as BatchStatus, error: undefined })), operation);

    // First attempt
    const firstResults = await submitAndPoll(batchesRef.current, operation);
    if (!isReversePromptOperationCurrent(operation)) return;

    // Apply first-attempt results
    commitBatches((prev) =>
      prev.map((b) => {
        const r = firstResults.get(b.index);
        if (!r) return b.status === "loading" ? { ...b, status: "error" as BatchStatus, error: "未返回结果" } : b;
        if (r.status === "done") return { ...b, status: "done" as BatchStatus, prompt: r.prompt || "" };
        return { ...b, status: "error" as BatchStatus, error: r.error };
      }), operation);

    // Collect failed items for auto-retry
    const failedIndices: number[] = [];
    firstResults.forEach((r, idx) => {
      if (r.status === "error") failedIndices.push(idx);
    });
    // Also include any that didn't get a result
    for (const b of batchesRef.current) {
      if (!firstResults.has(b.index) && !failedIndices.includes(b.index)) {
        failedIndices.push(b.index);
      }
    }

    if (failedIndices.length > 0) {
      // Auto-retry failed items once
      const retryBatches = batchesRef.current.filter((b) => failedIndices.includes(b.index));

      // Mark retry items as loading again
      commitBatches((prev) =>
        prev.map((b) =>
          failedIndices.includes(b.index)
            ? { ...b, status: "loading" as BatchStatus, error: undefined }
            : b
        ), operation);

      const retryResults = await submitAndPoll(retryBatches, operation);
      if (!isReversePromptOperationCurrent(operation)) return;

      // Apply retry results — failures stay as error, user can manually retry
      commitBatches((prev) =>
        prev.map((b) => {
          const r = retryResults.get(b.index);
          if (!r) return b;
          if (r.status === "done") return { ...b, status: "done" as BatchStatus, prompt: r.prompt || "" };
          return { ...b, status: "error" as BatchStatus, error: r.error };
        }), operation);
    }

    // Immediately release all buttons
    setAllLoading(false);
    const hasFailure = batchesRef.current.some((batch) => batch.status === "error");
    updateReversePromptOperation(operation, hasFailure ? "failed" : "completed", {
      batches: batchesRef.current,
      ...(hasFailure ? { errorCode: "E5302" } : {}),
    });
    releaseOperation(operation);
  }, [abortOperation, commitBatches, releaseOperation, reversePromptChannelPrefix, reversePromptOwnerScope, submitAndPoll]);

  const handleStopBatch = useCallback((batchIndex: number) => {
    const operation = batchOperationsRef.current.get(batchIndex);
    if (!operation) return;
    abortOperation(operation);
    const next = updateBatch(batchIndex, { status: "pending", error: undefined });
    updateReversePromptOperation(operation, "cancelled", { batches: next });
    releaseOperation(operation);
    clearReversePromptOperation(operation);
  }, [abortOperation, releaseOperation, updateBatch]);

  const handleStopAll = useCallback(() => {
    const operations = new Map<string, ReversePromptOperationClaim>();
    if (activeOperationRef.current) {
      operations.set(activeOperationRef.current.operationId, activeOperationRef.current);
    }
    for (const operation of batchOperationsRef.current.values()) {
      operations.set(operation.operationId, operation);
    }
    operations.forEach(abortOperation);
    const next = commitBatches((prev) =>
      prev.map((b) =>
        b.status === "loading" ? { ...b, status: "pending" as BatchStatus, error: undefined } : b
      )
    );
    operations.forEach((operation) => {
      updateReversePromptOperation(operation, "cancelled", { batches: next });
      releaseOperation(operation);
    });
    clearReversePromptOperations(reversePromptOwnerScope, reversePromptChannelPrefix);
    activeOperationRef.current = null;
    batchOperationsRef.current.clear();
    setAllLoading(false);
  }, [abortOperation, commitBatches, releaseOperation, reversePromptChannelPrefix, reversePromptOwnerScope]);

  const matchedCount = batches.filter((b) => b.status === "done" && b.prompt).length;
  const hasAnyPrompt = batches.some((b) => b.prompt.trim().length > 0);

  const handleApply = () => {
    const prompts = batchesRef.current.map((b) => b.prompt);
    onApplyPrompts(prompts);
    clearReversePromptOperations(reversePromptOwnerScope, reversePromptChannelPrefix);
    activeOperationRef.current = null;
  };

  if (!open) return null;

  const loadingCount = batches.filter((b) => b.status === "loading").length;
  const totalProgress = batches.length > 0
    ? Math.round(((matchedCount + loadingCount * 0.5) / batches.length) * 100)
    : 0;

  return (
    <div className="fixed inset-0 z-9999 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-[960px] max-h-[90vh] mx-4 rounded-2xl
                      border border-white/10 bg-[#0D0E11] shadow-[0_24px_80px_rgba(0,0,0,0.72)]
                      flex flex-col overflow-hidden">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-[#00FAE5]/55 to-transparent" />
        <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-white/8 bg-[#111216]/95">
          <div>
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg border border-[#00FAE5]/30 bg-[#00FAE5]/12 text-[#00FAE5] shadow-[0_0_18px_rgba(0,250,229,0.18)]">
                <Sparkles className="h-4 w-4" />
              </span>
              灵感提示词
            </h2>
            <p className="text-xs text-white/40 mt-1">
              根据参考图自动匹配提示词，支持删除/替换/拖拽排序图片
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg border border-white/10 bg-white/[0.04] p-1.5 text-white/50
                       transition-all hover:border-[#00FAE5]/35 hover:bg-[#00FAE5]/10 hover:text-[#00FAE5]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 bg-[#0D0E11]
                        [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-white/15
                        [&::-webkit-scrollbar-track]:bg-transparent">
          {/* Template Editor Section */}
          <div className="rounded-xl border border-white/10 bg-[#15161A] shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
            <button
              type="button"
              onClick={() => setTemplateExpanded(!templateExpanded)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors rounded-xl"
            >
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#00FAE5]" />
                <span className="text-sm font-medium text-white/80">提示词模板</span>
                {templateLoading ? (
                  <Loader2 className="w-3 h-3 text-white/40 animate-spin" />
                ) : (
                  <span className="text-[11px] text-[#00FAE5] px-2 py-0.5 rounded-full border border-[#00FAE5]/20 bg-[#00FAE5]/10">
                    {templateName || "未配置"}
                  </span>
                )}
              </div>
              {templateExpanded ? (
                <ChevronDown className="w-4 h-4 text-white/40" />
              ) : (
                <ChevronRight className="w-4 h-4 text-white/40" />
              )}
            </button>

            {templateExpanded && (
              <div className="px-4 pb-4 space-y-3 border-t border-white/8">
                {/* Template Selector */}
                {allTemplates.length > 1 && (
                  <div className="relative pt-3 mt-2" ref={templateSelectorRef}>
                    <label className="block text-[11px] text-white/40 mb-1">选择模板</label>
                    <button
                      type="button"
                      onClick={() => setShowTemplateSelector(!showTemplateSelector)}
                      className="w-full h-8 bg-[#0D0E11] rounded-lg px-3 text-sm text-left
                                 border border-white/10 hover:border-[#00FAE5]/35 transition-colors
                                 flex items-center justify-between"
                    >
                      <span className="text-white/70 truncate">{templateName || "请选择"}</span>
                      <ChevronDown className={`w-3 h-3 text-white/40 transition-transform ${showTemplateSelector ? "rotate-180" : ""}`} />
                    </button>
                    {showTemplateSelector && (
                      <div className="absolute z-50 mt-1 w-full bg-[#15161A] border border-white/15 rounded-lg py-1
                                      shadow-[0_12px_36px_rgba(0,0,0,0.55)] max-h-40 overflow-y-auto
                                      [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-white/15">
                        {allTemplates.map((tmpl) => (
                          <div
                            key={tmpl.id}
                            onClick={() => handleSelectTemplate(tmpl)}
                            className={`px-3 py-2 cursor-pointer transition-colors ${
                              tmpl.id === selectedTemplateId
                                ? "bg-[#00FAE5]/12 text-[#00FAE5]"
                                : "text-white/70 hover:bg-white/8 hover:text-white"
                            }`}
                          >
                            <div className="text-sm">{tmpl.template_name}</div>
                            {tmpl.description && (
                              <div className="text-[10px] text-white/30 mt-0.5 truncate">{tmpl.description}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

              </div>
            )}
          </div>

          {batches.map((batch) => (
            <BatchRow
              key={batch.index}
              batch={batch}
              componentLabels={componentLabels}
              onPromptChange={(p) => updateBatch(batch.index, { prompt: p })}
              onInspire={() => runSingleInspire(batch)}
              onStop={batch.status === "loading" ? () => handleStopBatch(batch.index) : undefined}
              onImagesChange={(imgs) => handleBatchImagesChange(batch.index, imgs)}
            />
          ))}

          {allLoading && (
            <div className="rounded-xl border border-[#00FAE5]/18 bg-[#15161A] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 text-[#00FAE5] animate-spin" />
                  <span className="text-sm text-white/70"><EcomShimmerText>正在分析参考图...</EcomShimmerText></span>
                </div>
                <button
                  onClick={handleStopAll}
                  className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium
                             text-red-300 bg-red-500/15 border border-red-500/25
                             hover:bg-red-500/25 hover:border-red-500/40 transition-all"
                >
                  <Square className="w-3 h-3 fill-current" />
                  停止分析
                </button>
              </div>
              <div className="w-full h-1.5 bg-white/8 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#00FAE5] rounded-full transition-all duration-500 shadow-[0_0_14px_rgba(0,250,229,0.45)]"
                  style={{ width: `${totalProgress}%` }}
                />
              </div>
              <p className="text-[11px] text-white/30 mt-2"><EcomShimmerText>分析中 {totalProgress}%...</EcomShimmerText></p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-white/8 bg-[#111216]/95">
          <span className="text-xs text-white/40">
            共 {batches.length} 条，已匹配 {matchedCount} 条
            {hasAnyPrompt && matchedCount < batches.filter(b => b.prompt.trim()).length && (
              <span className="text-white/30">（含手动编辑 {batches.filter(b => b.prompt.trim()).length - matchedCount} 条）</span>
            )}
          </span>
          <div className="flex items-center gap-3">
            {allLoading ? (
              <button
                onClick={handleStopAll}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white
                           bg-red-500/20 border border-red-500/30
                           hover:bg-red-500/30 hover:border-red-500/40 transition-all
                           flex items-center gap-1.5"
              >
                <Square className="w-3.5 h-3.5 fill-current" /> 停止分析
              </button>
            ) : (
              <button
                onClick={runAllInspire}
                disabled={batches.length === 0}
                className="group relative flex items-center gap-1.5 overflow-hidden rounded-lg px-4 py-2 text-sm font-medium text-white
                           bg-[url('/button_bg6.png')] bg-[length:100%_100%] bg-center bg-no-repeat
                           transition-[filter,opacity] hover:brightness-110
                           disabled:cursor-not-allowed disabled:opacity-40"
              >
                <div className="absolute inset-0 bg-linear-to-r from-transparent via-white/10 to-transparent
                                -translate-x-full transition-transform duration-700 ease-in-out group-hover:translate-x-full" />
                <Sparkles className="relative z-10 w-3.5 h-3.5" />
                <span className="relative z-10">全部反推</span>
              </button>
            )}
            <button
              onClick={handleApply}
              disabled={!hasAnyPrompt}
              className="px-4 py-2 rounded-lg border border-[#F6F6F6] bg-[#F6F6F6] text-sm font-semibold text-[#181818]
                         hover:bg-white transition-colors
                         disabled:opacity-40 disabled:cursor-not-allowed"
            >
              应用提示词
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ====== Sub-component: BatchRow ======

function StatusBadge({ status }: { status: BatchStatus }) {
  switch (status) {
    case "pending":
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium
                         border border-white/10 bg-white/[0.06] text-white/55">
          待反推
        </span>
      );
    case "loading":
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium
                         border border-[#00FAE5]/25 bg-[#00FAE5]/10 text-[#00FAE5]">
          <Loader2 className="w-3 h-3 animate-spin" /> 反推中
        </span>
      );
    case "done":
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium
                         border border-[#00FAE5]/25 bg-[#00FAE5]/10 text-[#00FAE5]">
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          已匹配
        </span>
      );
    case "error":
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium
                         border border-red-500/25 bg-red-500/15 text-red-300">
          失败
        </span>
      );
  }
}

// ====== Sub-component: BatchRow with image management ======

function BatchRow({
  batch,
  componentLabels,
  onPromptChange,
  onInspire,
  onStop,
  onImagesChange,
}: {
  batch: BatchItem;
  componentLabels?: Record<string, string>;
  onPromptChange: (p: string) => void;
  onInspire: () => void;
  onStop?: () => void;
  onImagesChange: (images: BatchImage[]) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const [reorderSrcIdx, setReorderSrcIdx] = useState<number | null>(null);
  const [dropZoneActive, setDropZoneActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const replaceIdxRef = useRef<number | null>(null);

  const uploadAndReplace = useCallback(async (file: File, replaceIndex?: number) => {
    if (!isImageFile(file)) return;
    setUploading(true);
    try {
      const res = await ecomImageApi.uploadProductImage(file);
      if (res.data?.file_url) {
        const url = res.data.file_url;
        const compKey = batch.images[0]?.componentKey || "uploaded";
        if (replaceIndex !== undefined && replaceIndex >= 0 && replaceIndex < batch.images.length) {
          const next = [...batch.images];
          next[replaceIndex] = { componentKey: next[replaceIndex].componentKey, url };
          onImagesChange(next);
        } else {
          onImagesChange([...batch.images, { componentKey: compKey, url }]);
        }
      }
    } catch {
      // silently fail
    } finally {
      setUploading(false);
    }
  }, [batch.images, onImagesChange]);

  const handleDeleteImage = useCallback((idx: number) => {
    const next = batch.images.filter((_, i) => i !== idx);
    onImagesChange(next);
  }, [batch.images, onImagesChange]);

  const handleReorderDragStart = useCallback((e: React.DragEvent, idx: number) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/x-batch-reorder", String(idx));
    setReorderSrcIdx(idx);
  }, []);

  const handleReorderDragOver = useCallback((e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (reorderSrcIdx !== null) {
      e.dataTransfer.dropEffect = "move";
      setDragOverIdx(idx);
    } else if (e.dataTransfer.types.includes("Files")) {
      e.dataTransfer.dropEffect = "copy";
      setDragOverIdx(idx);
    }
  }, [reorderSrcIdx]);

  const handleReorderDrop = useCallback((e: React.DragEvent, targetIdx: number) => {
    e.preventDefault();
    e.stopPropagation();

    const srcStr = e.dataTransfer.getData("text/x-batch-reorder");
    if (srcStr) {
      const srcIdx = parseInt(srcStr, 10);
      if (!isNaN(srcIdx) && srcIdx !== targetIdx) {
        const next = [...batch.images];
        const [moved] = next.splice(srcIdx, 1);
        next.splice(targetIdx, 0, moved);
        onImagesChange(next);
      }
    } else if (e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      uploadAndReplace(file, targetIdx);
    }

    setReorderSrcIdx(null);
    setDragOverIdx(null);
    setDropZoneActive(false);
  }, [batch.images, onImagesChange, uploadAndReplace]);

  const handleReorderDragEnd = useCallback(() => {
    setReorderSrcIdx(null);
    setDragOverIdx(null);
  }, []);

  const handleAddAreaDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDropZoneActive(false);
    if (e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      uploadAndReplace(file);
    }
  }, [uploadAndReplace]);

  const handleReplaceClick = useCallback((idx: number) => {
    replaceIdxRef.current = idx;
    fileInputRef.current?.click();
  }, []);

  const handleAddClick = useCallback(() => {
    replaceIdxRef.current = null;
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadAndReplace(file, replaceIdxRef.current ?? undefined);
    }
    e.target.value = "";
  }, [uploadAndReplace]);

  return (
    <div className="rounded-xl border border-white/10 bg-[#15161A] overflow-hidden shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
      <input
        ref={fileInputRef}
        type="file"
        accept={IMAGE_ACCEPT}
        className="absolute w-0 h-0 opacity-0 overflow-hidden pointer-events-none"
        tabIndex={-1}
        aria-hidden="true"
        onChange={handleFileChange}
      />

      <div className="flex items-center justify-between px-4 pt-3 pb-2">
        <StatusBadge status={batch.status} />
        <div className="flex items-center gap-1.5">
          {batch.status === "loading" && onStop && (
            <button
              onClick={onStop}
              className="px-2.5 py-1 rounded-md text-xs font-medium text-red-300
                         bg-red-500/15 border border-red-500/25
                         hover:bg-red-500/25 hover:border-red-500/40 transition-all
                         flex items-center gap-1"
            >
              <Square className="w-2.5 h-2.5 fill-current" />
              停止
            </button>
          )}
          <button
            onClick={onInspire}
            disabled={batch.status === "loading" || batch.images.length === 0}
            className="px-3 py-1 rounded-md text-xs font-medium text-[#E6E6E6]
                       bg-white/8 hover:bg-[#00FAE5]/10 border border-white/10 hover:border-[#00FAE5]/35 hover:text-[#00FAE5]
                       disabled:opacity-30 disabled:cursor-not-allowed transition-all
                       flex items-center gap-1"
          >
            {batch.status === "loading" ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Sparkles className="w-3 h-3" />
            )}
            反推
          </button>
        </div>
      </div>

      {/* Image strip - horizontal scroll */}
      <div className="px-4 pb-3">
        {batch.images.length > 0 ? (
          <div className="flex gap-2 items-start overflow-x-auto pb-2
                          [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:bg-white/15
                          [&::-webkit-scrollbar-track]:bg-transparent">
            {batch.images.map((img, idx) => (
              <div
                key={idx}
                draggable
                onDragStart={(e) => handleReorderDragStart(e, idx)}
                onDragOver={(e) => handleReorderDragOver(e, idx)}
                onDrop={(e) => handleReorderDrop(e, idx)}
                onDragEnd={handleReorderDragEnd}
                onDragLeave={() => setDragOverIdx(null)}
                className={`relative shrink-0 w-[80px] h-[100px] rounded-lg overflow-hidden border group/thumb
                            cursor-grab active:cursor-grabbing transition-all duration-150
                            ${reorderSrcIdx === idx ? "opacity-30 scale-90" : ""}
                            ${dragOverIdx === idx && reorderSrcIdx !== null && reorderSrcIdx !== idx
                              ? "ring-2 ring-white/80 scale-105 border-white/80"
                              : dragOverIdx === idx && reorderSrcIdx === null
                                ? "ring-2 ring-white/80 scale-105 border-white/80"
                                : "border-white/15"
                            }
                            bg-black/30`}
              >
                <img
                  src={img.url}
                  alt={`${img.componentKey} batch ${batch.index + 1}`}
                  className="w-full h-full object-cover pointer-events-none select-none"
                  draggable={false}
                />

                {componentLabels?.[img.componentKey] && (
                  <div className="absolute top-0 inset-x-0 bg-black/60 backdrop-blur-xs py-0.5 text-center pointer-events-none z-5">
                    <span className="text-[8px] font-medium text-white/80">{componentLabels[img.componentKey]}</span>
                  </div>
                )}

                {dragOverIdx === idx && reorderSrcIdx === null && (
                  <div className="absolute inset-0 bg-white/20 flex items-center justify-center pointer-events-none z-10">
                    <Upload className="w-4 h-4 text-white" />
                  </div>
                )}

                <div className="absolute top-0 left-0 right-0 flex items-center justify-between
                                opacity-0 group-hover/thumb:opacity-100 transition-opacity z-10">
                  <div className="p-0.5 cursor-grab" title="拖拽排序">
                    <GripVertical className="w-3 h-3 text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]" />
                  </div>
                </div>

                <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1 py-1
                                bg-linear-to-t from-black/80 to-transparent
                                opacity-0 group-hover/thumb:opacity-100 transition-opacity z-10">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleReplaceClick(idx); }}
                    className="w-6 h-6 rounded-full bg-black/55 backdrop-blur-xs
                               flex items-center justify-center text-white/85
                               border border-white/15 hover:border-white/45 hover:bg-white/18 hover:text-white transition-all"
                    title="替换图片"
                  >
                    <Upload className="w-2.5 h-2.5" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteImage(idx); }}
                    className="w-6 h-6 rounded-full bg-black/55 backdrop-blur-xs
                               flex items-center justify-center text-white/85
                               border border-white/15 hover:border-red-400/50 hover:bg-red-500/75 hover:text-white transition-all"
                    title="删除图片"
                  >
                    <Trash2 className="w-2.5 h-2.5" />
                  </button>
                </div>

                {uploading && replaceIdxRef.current === idx && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-20">
                    <Loader2 className="w-4 h-4 text-white animate-spin" />
                  </div>
                )}
              </div>
            ))}

            <div
              onDrop={handleAddAreaDrop}
              onDragOver={(e) => { e.preventDefault(); setDropZoneActive(true); }}
              onDragLeave={() => setDropZoneActive(false)}
              onClick={handleAddClick}
              className={`shrink-0 w-[80px] h-[100px] rounded-lg border border-dashed flex flex-col items-center justify-center
                          gap-1 cursor-pointer transition-all
                          ${dropZoneActive
                            ? "border-white/70 bg-white/10"
                            : "border-white/15 hover:border-white/35 hover:bg-white/8"
                          }`}
            >
              {uploading && replaceIdxRef.current === null ? (
                <Loader2 className="w-4 h-4 text-white/40 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 text-white/45" />
              )}
              <span className="text-[9px] text-white/30">添加</span>
            </div>
          </div>
        ) : (
          <div
            onDrop={handleAddAreaDrop}
            onDragOver={(e) => { e.preventDefault(); setDropZoneActive(true); }}
            onDragLeave={() => setDropZoneActive(false)}
            onClick={handleAddClick}
            className={`w-[80px] h-[100px] rounded-lg border border-dashed flex flex-col items-center justify-center
                        gap-1 cursor-pointer transition-all
                        ${dropZoneActive
                          ? "border-white/70 bg-white/10"
                          : "border-white/15 hover:border-white/35 hover:bg-white/8"
                        }`}
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 text-white/40 animate-spin" />
            ) : (
              <Plus className="w-4 h-4 text-white/45" />
            )}
            <span className="text-[9px] text-white/30">{uploading ? "上传中" : "上传图片"}</span>
          </div>
        )}
      </div>

      {/* Prompt area - below images */}
      <div className="px-4 pb-4 space-y-2">
        {batch.status === "done" && batch.prompt ? (
          <AutoResizeTextarea
            id={`batch-prompt-${batch.index}`}
            data-ecom-prompt-input
            value={batch.prompt}
            onChange={(e) => onPromptChange(e.target.value)}
            minHeight={80}
            className="w-full bg-[#0D0E11] rounded-lg border border-white/10 px-3 py-2
                       text-sm text-white/80 leading-normal outline-hidden
                       focus:border-[#00FAE5]/35 transition-colors
                       [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-white/15
                       [&::-webkit-scrollbar-track]:bg-transparent"
          />
        ) : (
          <AutoResizeTextarea
            data-ecom-prompt-input
            value={batch.prompt}
            onChange={(e) => onPromptChange(e.target.value)}
            placeholder="反推结果将显示在这里，也可手动编辑..."
            minHeight={60}
            className="w-full bg-[#0D0E11] rounded-lg border border-white/10
                       px-3 py-2 text-sm leading-normal text-white placeholder:text-white/25
                       outline-hidden focus:border-[#00FAE5]/35 transition-colors
                       [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-white/15
                       [&::-webkit-scrollbar-track]:bg-transparent"
          />
        )}

        {batch.status === "loading" && (
          <div className="space-y-1">
            <div className="w-full h-1 bg-white/8 rounded-full overflow-hidden">
              <div className="h-full bg-[#00FAE5] rounded-full shadow-[0_0_12px_rgba(0,250,229,0.45)]
                              animate-[indeterminate_1.5s_ease-in-out_infinite]"
                   style={{ width: "40%" }} />
            </div>
          </div>
        )}

        {batch.error && (
          <p className="text-[11px] text-red-400">{batch.error}</p>
        )}
      </div>
    </div>
  );
}
